import React from 'react';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MetricCard from './MetricCard';
import { AlertTriangle, Target, Shield, Package } from "lucide-react";

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: '28px',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
}));

// Calculate business metrics from props
const BusinessPerformance = ({ stats, subscription, issueTrends, workTypeDistribution, projects = [], selectedProject = "" }) => {
  // Determine if current project uses Azure Boards
  const currentProject = projects.find(p => p.api_key === selectedProject);
  const isAzureBoards = currentProject?.azure_boards_enabled || false;
  
  // Use appropriate work type distribution based on project type
  const activeWorkTypeDistribution = isAzureBoards 
    ? (stats?.azureBoardsWorkTypeDistribution || {})
    : workTypeDistribution;
  // Extract work type counts from the comprehensive distribution data
  const totalStories = activeWorkTypeDistribution?.stories?.count || 0;
  const totalChores = activeWorkTypeDistribution?.chores?.count || 0;
  const totalBugs = activeWorkTypeDistribution?.bugs?.count || 0;
  const totalEpics = activeWorkTypeDistribution?.epics?.count || 0;
  const totalServiceRequests = activeWorkTypeDistribution?.service_requests?.count || 0;
  const totalIncidents = activeWorkTypeDistribution?.incidents?.count || 0;
  const totalProblems = activeWorkTypeDistribution?.problems?.count || 0;
  const totalChanges = activeWorkTypeDistribution?.changes?.count || 0;
  const totalSubTasks = activeWorkTypeDistribution?.sub_tasks?.count || 0;
  const totalTesting = activeWorkTypeDistribution?.testing?.count || 0;
  const totalReviews = activeWorkTypeDistribution?.reviews?.count || 0;
  const totalMeetings = activeWorkTypeDistribution?.meetings?.count || 0;
  const totalUnknown = activeWorkTypeDistribution?.unknown?.count || 0;
  
  // Calculate total work items delivered (all types)
  const totalWorkItems = totalStories + totalChores + totalBugs + totalEpics + 
                        totalServiceRequests + totalIncidents + totalProblems + 
                        totalChanges + totalSubTasks + totalTesting + 
                        totalReviews + totalMeetings + totalUnknown;
  
  const deploymentFrequency = stats?.deploymentFrequency || 0;
  const deploymentTrendPct = typeof stats?.deploymentFrequencyTrendPct === 'number' ? stats.deploymentFrequencyTrendPct : 0;
  const deploymentTrend = deploymentTrendPct > 0 ? 'up' : deploymentTrendPct < 0 ? 'down' : 'up';
  const deploymentChange = `${deploymentTrendPct > 0 ? '+' : deploymentTrendPct < 0 ? '' : '+'}${Math.abs(deploymentTrendPct).toFixed(1)}%`;
  const deploymentHasPrev = !!stats?.deploymentFrequencyHasPrev;
  const deploymentDelta = Number(stats?.deploymentFrequencyDelta || 0);
  const deploymentFooter = deploymentHasPrev ? `Δ ${deploymentDelta > 0 ? '+' : deploymentDelta < 0 ? '' : '+'}${Math.abs(deploymentDelta).toFixed(0)} vs previous period` : 'No previous data';
  const changeFailureRate = 0; // TODO: wire when backend supplies

  const cirr = typeof stats?.criticalIssuesResolutionRate === 'number' ? stats.criticalIssuesResolutionRate : 0;
  const cirrTrendPct = typeof stats?.criticalIssuesResolutionTrendPct === 'number' ? stats.criticalIssuesResolutionTrendPct : 0;
  const cirrTrend = cirrTrendPct > 0 ? 'up' : cirrTrendPct < 0 ? 'down' : 'up';
  const cirrChange = `${cirrTrendPct > 0 ? '+' : cirrTrendPct < 0 ? '' : '+'}${Math.abs(cirrTrendPct).toFixed(1)}%`;
  const cirrHasPrev = !!stats?.criticalIssuesResolutionHasPrev;
  const cirrDelta = Number(stats?.criticalIssuesResolutionDelta || 0);
  const cirrFooter = cirrHasPrev ? `${Math.abs(cirrDelta).toFixed(1)} pts vs previous period` : 'No previous data';

  // Use total from distribution if available, otherwise calculate from counts, otherwise use stats
  const workTypeTotal = activeWorkTypeDistribution?.total || totalWorkItems || Number(stats?.workTypeTotal || 0);
  const workTypeTrendPct = typeof stats?.workTypeTotalTrendPct === 'number' ? stats.workTypeTotalTrendPct : 0;
  const workTypeTrend = workTypeTrendPct > 0 ? 'up' : workTypeTrendPct < 0 ? 'down' : 'up';
  const workTypeChange = `${workTypeTrendPct > 0 ? '+' : workTypeTrendPct < 0 ? '' : '+'}${Math.abs(workTypeTrendPct).toFixed(1)}%`;
  const workTypeHasPrev = !!stats?.workTypeTotalHasPrev;
  const workTypeDelta = Number(stats?.workTypeTotalDelta || 0);
  const workTypeFooter = workTypeHasPrev ? `${Math.abs(workTypeDelta).toFixed(0)} ${workTypeDelta > 0 ? 'more' : workTypeDelta < 0 ? 'fewer' : ''} items vs previous period` : 'No previous data';

  const businessMetrics = [
    {
      title: isAzureBoards ? "Work Items / Issues / Bugs Delivered" : "Stories / Chores / Bugs Delivered",
      subtitle: isAzureBoards ? "Azure Boards Work Distribution" : "Comprehensive Work Distribution",
      value: workTypeTotal,
      description: "Work Distribution",
      icon: <Target />,
      change: workTypeChange,
      trend: workTypeTrend,
      trendText: workTypeFooter,
      iconBgColor: "#fff",
      breakdown: [
        `${totalStories} Stories`,
        `${totalChores} Chores`,
        `${totalBugs} Bugs`,
        `${totalEpics} Epics`,
        `${totalServiceRequests} Service Requests`,
        `${totalIncidents} Incidents`,
        `${totalProblems} Problems`,
        `${totalChanges} Changes`,
        `${totalSubTasks} Sub-tasks`,
        `${totalTesting} Testing`,
        `${totalReviews} Reviews`,
        `${totalMeetings} Meetings`,
        `${totalUnknown} Unknown`,
      ].filter(item => !item.startsWith('0 ')), // Only show non-zero counts
    },
    {
      title: "Deployment Frequency",
      subtitle: "Delivery Speed",
      value: deploymentFrequency,
      description: "Deployments This Period",
      icon: <Package />,
      change: deploymentChange,
      trend: deploymentTrend,
      invertColors: false,
      trendText: deploymentFooter,
      iconBgColor: "#fff",
    },
    // {
    //   title: "Change Failure Rate (CFR)",
    //   subtitle: "Quality Assurance",
    //   value: `${changeFailureRate}%`,
    //   description: "Post-deployment Issues",
    //   icon: <Shield />,
    //   change: "+0.0%",
    //   trend: "up",
    //   trendText: "0.0% — improving",
    //   iconBgColor: "#fff",
    // },
    {
      title: "Critical Issues Resolution Rate",
      subtitle: "Response Efficiency",
      value: `${cirr.toFixed(1)}%`,
      description: "Fixes on critical Issues",
      icon: <AlertTriangle />,
      change: cirrChange,
      trend: cirrTrend,
      trendText: cirrFooter,
      iconBgColor: "#fff",
    },
  ];
  return (
    <SectionContainer>
      <SectionHeader>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#020817',
            fontSize: '1.5rem',
            lineHeight: '2rem',
          }}
        >
          Business Performance
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#6b7280',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          }}
        >
          Strategic delivery metrics
        </Typography>
      </SectionHeader>

      <Grid container spacing={3}>
        {businessMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} lg={4} key={index} sx={{ display: 'flex' }}>
            <MetricCard
              title={metric.title}
              value={metric.value}
              description={metric.description}
              icon={metric.icon}
              change={metric.change}
              trend={metric.trend}
              trendText={metric.trendText}
              iconBgColor={metric.iconBgColor}
              breakdown={metric.breakdown}
              sx={{ width: '100%' }}
            />
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );
};

export default BusinessPerformance;